/*

  EloPage.ts

  Elo status list component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { EloListSelection, IView, UserListItem } from "./types";
import { Component, ComponentFunc, VnodeChildren, m } from "./mithril";
import { ts, mt } from "./i18n";
import { glyph, glyphGrayed, nbsp } from "./util";
import { ChallengeButton, UserInfoButton } from "./components";

const EloSelector: Component<
{
  sel: EloListSelection;
  onchange: (sel: EloListSelection) => void;
},
{
  sel: EloListSelection;
}
> = {
  oninit: (vnode) => {
    vnode.state.sel = vnode.attrs.sel;
  },
  view: (vnode) => {
    return m(".toggler[id='elo-toggler']", { title: ts("elo_list_choice") },
      [
        m(".option.x-small",
          {
            // Show ranking for human games only
            className: (vnode.state.sel === "human" ? "selected" : ""),
            onclick: (ev: Event) => {
              vnode.state.sel = "human";
              vnode.attrs.onchange(vnode.state.sel);
              ev.preventDefault();
            },
          },
          glyph("user")
        ),
        m(".option.x-small",
          {
            // Show ranking for all games, including robots
            className: (vnode.state.sel === "all" ? "selected" : ""),
            onclick: (ev: Event) => {
              vnode.state.sel = "all";
              vnode.attrs.onchange(vnode.state.sel);
              ev.preventDefault();
            },
          },
          glyph("cog")
        ),
        m(".option.x-small",
          {
            // Show ranking for manual games only
            className: (vnode.state.sel === "manual" ? "selected" : ""),
            onclick: (ev: Event) => {
              vnode.state.sel = "manual";
              vnode.attrs.onchange(vnode.state.sel);
              ev.preventDefault();
            },
          },
          glyph("lightbulb")
        )
      ]
    );
  }
};

interface IAttributes {
  view: IView;
  spec: EloListSelection | null;
  id: string;
  key: string;
}

export const EloPage: ComponentFunc<IAttributes> = (initialVnode) => {

  // Show the header of an Elo ranking list and then the list itself

  const { spec, view } = initialVnode.attrs;

  return {
    view: (vnode) => {
      return m("div", [
        m(".listitem.listheader",
          [
            m("span.list-ch", glyphGrayed("hand-right", { title: ts('Skora á') })),
            mt("span.list-rank", "Röð"),
            m("span.list-rank-no-mobile", { title: ts('Röð í gær') }, ts("1d")),
            m("span.list-rank-no-mobile", { title: ts('Röð fyrir viku') }, ts("7d")),
            mt("span.list-nick-elo", "Einkenni"),
            m("span.list-elo", { title: ts('Elo-stig') }, ts("Elo")),
            m("span.list-elo-no-mobile", { title: ts('Elo-stig í gær') }, ts("1d")),
            m("span.list-elo-no-mobile", { title: ts('Elo-stig fyrir viku') }, ts("7d")),
            m("span.list-elo-no-mobile", { title: ts('Elo-stig fyrir mánuði') }, ts("30d")),
            m("span.list-games", { title: ts('Fjöldi viðureigna') }, glyph("th")),
            m("span.list-ratio", { title: ts('Vinningshlutfall') }, glyph("bookmark")),
            m("span.list-avgpts", { title: ts('Meðalstigafjöldi') }, glyph("dashboard")),
            mt("span.list-info-hdr", "Ferill"),
            m(EloSelector,
              {
                sel: spec || "human",
                onchange: (s: EloListSelection) => {
                  view.actions.withSpinner(
                    () => view.model.loadEloRatingList(s)
                  );
                },
              }
            ),
          ]
        ),
        m(EloList,
          {
            id: vnode.attrs.id,
            sel: spec,
            view: vnode.attrs.view,
            // key: "elolist",
          }
        )
      ]);
    }
  };

};

const EloList: ComponentFunc<{
  view: IView;
  id: string;
  sel: EloListSelection | null;
}> = (initialVnode) => {

  const outerView = initialVnode.attrs.view;
  const model = outerView.model;
  const state = model.state;

  return {

    view: (vnode) => {

      function itemize(item: UserListItem, i: number) {

        // Generate a list item about a user in an Elo ranking table

        const isRobot = item.userid.indexOf("robot-") === 0;

        function rankStr(rank: number, ref?: number): string {
          // Return a rank string or dash if no rank or not meaningful
          // (i.e. if the reference, such as the number of games, is zero)
          if (rank === 0 || (ref !== undefined && ref === 0))
            return "--";
          return rank.toString();
        }

        let nick: VnodeChildren = item.nick;
        let ch = m("span.list-ch", nbsp());
        const userId = state?.netskraflUserId ?? "";
        if (item.userid != userId && !item.inactive) {
          ch = m(ChallengeButton, { view: outerView, item });
        }
        if (isRobot) {
          nick = m("span", [glyph("cog"), nbsp(), nick]);
        }
        if (item.fairplay && !isRobot)
          nick = m("span",
            [m("span.fairplay-btn", { title: ts("Skraflar án hjálpartækja") }), nick]);

        return m(".listitem",
          {
            key: (vnode.attrs.sel || "sel") + i,
            className: (i % 2 === 0 ? "oddlist" : "evenlist")
          },
          [
            ch, // Challenge icon (right-hand)
            m("span.list-rank.bold", rankStr(item.rank)),
            m("span.list-rank-no-mobile", rankStr(item.rank_yesterday)),
            m("span.list-rank-no-mobile", rankStr(item.rank_week_ago)),
            m("span.list-nick-elo", { title: item.fullname }, nick),
            m("span.list-elo.bold", item.elo),
            m("span.list-elo-no-mobile", rankStr(item.elo_yesterday, item.games_yesterday)),
            m("span.list-elo-no-mobile", rankStr(item.elo_week_ago, item.games_week_ago)),
            m("span.list-elo-no-mobile", rankStr(item.elo_month_ago, item.games_month_ago)),
            m("span.list-games.bold", item.games >= 100000 ? Math.round(item.games / 1000) + "K" : item.games),
            m("span.list-ratio", item.ratio + "%"),
            m("span.list-avgpts", item.avgpts),
            item.userid === userId ? nbsp() : m(UserInfoButton, { view: outerView, item }),
          ]
        );
      }

      const list: UserListItem[] = model.eloRatingList || [];
      return m("div", { id: vnode.attrs.id }, list.map(itemize));
    }

  };
};
